import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    alpha,
    Box,
    Button,
    Chip,
    Container,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    CircularProgress,
} from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { Link as RouterLink } from "react-router-dom";
import { fetchStaffAcademicCalendar } from "../../utils/academicPeriodApi";
import { defaultAcademicYearMeta, displayTermStatus } from "../../utils/currentTermUtils";

function formatDisplayDate(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Shared academic calendar for admin, teachers, and form teachers.
 * Read-only — no fee or financial data.
 */
export default function AcademicCalendarView({ schoolId, schoolType, adminMode = false, managePath }) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [educationLevel, setEducationLevel] = useState(
        schoolType === "Primary" ? "Primary" : "Secondary"
    );
    const [academicYear, setAcademicYear] = useState(defaultAcademicYearMeta().label);

    const yearOptions = useMemo(() => {
        const meta = defaultAcademicYearMeta();
        return [
            `${meta.startYear - 1}/${meta.startYear}`,
            meta.label,
            `${meta.endYear}/${meta.endYear + 1}`,
        ];
    }, []);

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        setError("");
        try {
            const calendar = await fetchStaffAcademicCalendar(schoolId, {
                educationLevel: schoolType === "Combined" ? educationLevel : undefined,
                academicYear,
            });
            setData(calendar);
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Could not load academic calendar.");
        } finally {
            setLoading(false);
        }
    }, [schoolId, schoolType, educationLevel, academicYear]);

    useEffect(() => {
        load();
    }, [load]);

    const current = data?.current;
    const terms = data?.terms || [];
    const deadlines = data?.deadlines || [];
    const exams = data?.exams || [];
    const events = data?.events || [];

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[50]} 32%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
                    <Box>
                        <Typography variant="overline" color="text.secondary">
                            School calendar
                        </Typography>
                        <Typography variant="h4" fontWeight={700}>
                            Academic calendar
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, mt: 0.5 }}>
                            Current term dates, key deadlines, exams, and events. Teaching staff see dates only — no
                            financial information.
                        </Typography>
                    </Box>
                    {adminMode && managePath ? (
                        <Button variant="contained" component={RouterLink} to={managePath}>
                            Manage years & terms
                        </Button>
                    ) : null}
                </Stack>

                {error ? (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        {error}
                    </Alert>
                ) : null}

                <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap>
                        {schoolType === "Combined" ? (
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Section</InputLabel>
                                <Select label="Section" value={educationLevel} onChange={(e) => setEducationLevel(e.target.value)}>
                                    <MenuItem value="Primary">Primary</MenuItem>
                                    <MenuItem value="Secondary">Secondary</MenuItem>
                                </Select>
                            </FormControl>
                        ) : null}
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Academic year</InputLabel>
                            <Select label="Academic year" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                                {yearOptions.map((y) => (
                                    <MenuItem key={y} value={y}>
                                        {y}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {current?.label ? (
                            <Chip
                                icon={<EventAvailableOutlinedIcon />}
                                label={`Current: ${current.label}`}
                                color="primary"
                                variant="outlined"
                                sx={{ fontWeight: 700 }}
                            />
                        ) : null}
                    </Stack>
                </Paper>

                {loading && !data ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={7}>
                            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden", mb: 2 }}>
                                <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <CalendarMonthOutlinedIcon color="primary" />
                                        <Typography fontWeight={700}>Term dates</Typography>
                                    </Stack>
                                </Box>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Term</TableCell>
                                            <TableCell>Opens</TableCell>
                                            <TableCell>Closes</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {terms.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                        Term dates have not been set yet.
                                                        {adminMode ? " Use Manage years & terms to configure." : " Contact the school office."}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            terms.map((t) => {
                                                const chip = displayTermStatus(t);
                                                return (
                                                    <TableRow key={t._id || t.id}>
                                                        <TableCell sx={{ fontWeight: 600 }}>{t.label}</TableCell>
                                                        <TableCell>{formatDisplayDate(t.openDate)}</TableCell>
                                                        <TableCell>{formatDisplayDate(t.closeDate)}</TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                size="small"
                                                                label={chip.label}
                                                                color={chip.color}
                                                                variant={chip.variant}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </Paper>

                            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                                <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
                                    <Typography fontWeight={700}>Upcoming deadlines</Typography>
                                </Box>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Deadline</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {deadlines.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={2}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                                        No deadlines configured.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            deadlines.map((d, i) => (
                                                <TableRow key={`${d.type}-${d.date}-${i}`}>
                                                    <TableCell>{formatDisplayDate(d.date)}</TableCell>
                                                    <TableCell>{d.label}</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 2 }}>
                                <Typography fontWeight={700} gutterBottom>
                                    Exam timetable
                                </Typography>
                                {exams.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No exams scheduled for this year yet.
                                    </Typography>
                                ) : (
                                    <Stack spacing={1}>
                                        {exams.slice(0, 8).map((e) => (
                                            <Box key={e.id || e._id}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {e.examName || e.title || "Exam"} · {formatDisplayDate(e.date)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {[e.subjectName, e.session, e.startTime].filter(Boolean).join(" · ")}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                                <Typography fontWeight={700} gutterBottom>
                                    School events
                                </Typography>
                                {events.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No events listed for this year.
                                    </Typography>
                                ) : (
                                    <Stack spacing={1}>
                                        {events.slice(0, 8).map((e) => (
                                            <Box key={e.id || e._id}>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {e.title} · {formatDisplayDate(e.date)}
                                                </Typography>
                                                {e.category ? (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {e.category}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Paper>
                        </Grid>
                    </Grid>
                )}
            </Container>
        </Box>
    );
}
